import { useState, useEffect, useMemo } from 'react';
import { useHistory } from 'react-router-dom';
import { useAuth } from '../../../AuthContext';
import { fetchData } from '../../../apiService';
import LogoLoader from '../../common/LogoLoader';
import SearchableDropdown from '../../common/SearchableDropdown';
import { useSessionValidation } from '../../../utils/sessionValidation';
import { Refresh, GetApp, ArrowBack } from '@mui/icons-material';
import { IconButton, Tooltip, Alert, Snackbar } from '@mui/material';
import { exportStyledExcel } from '../../../utils/excelExport';

const PRIMARY     = '#00695c';
const PRIMARY_MID = '#00897b';
const PRIMARY_LT  = '#e0f2f1';

const fmtQ = (n) => (parseFloat(n) || 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const fmt2Date = (date) => {
    const y = date.getFullYear();
    const m = String(date.getMonth() + 1).padStart(2, '0');
    const d = String(date.getDate()).padStart(2, '0');
    return `${y}-${m}-${d}`;
};
const fmtDate = (d) => {
    if (!d) return '-';
    const p = new Date(d);
    if (isNaN(p)) return d;
    return `${String(p.getDate()).padStart(2,'0')}/${String(p.getMonth()+1).padStart(2,'0')}/${p.getFullYear()}`;
};

const iconBtn = {
    border: '1px solid rgba(255,255,255,0.3)', borderRadius: '8px',
    height: 29, width: 29, color: '#fff', flexShrink: 0,
    '&:hover': { background: 'rgba(255,255,255,0.1)' },
    '&.Mui-disabled': { color: 'rgba(255,255,255,0.3)' },
};

const TRAN_COLORS = {
    PURCHASE: '#059669', SALE: '#0277bd', 'SALE RETURN': '#dc2626',
    'PURCHASE RETURN': '#f59e0b', 'STOCK IN': '#7c3aed', 'STOCK OUT': '#dc2626',
    TRANSFER: '#0891b2',
};

const StockLedger = () => {
    useSessionValidation();
    const { companyId, branchId, token, companyName, username } = useAuth();
    const history = useHistory();

    const today = new Date();
    const [fromDate, setFromDate]   = useState(fmt2Date(new Date(today.getFullYear(), today.getMonth(), 1)));
    const [toDate, setToDate]       = useState(fmt2Date(today));
    const [items, setItems]         = useState([]);
    const [itemId, setItemId]       = useState('0');
    const [data, setData]           = useState([]);
    const [loading, setLoading]     = useState(false);
    const [error, setError]         = useState('');
    const [fetched, setFetched]     = useState(false);
    const [search, setSearch]       = useState('');

    useEffect(() => {
        if (!companyId || !token) return;
        fetchData('ItemApi', `/${companyId}`, token)
            .then(res => setItems(res?.response_data || res || []))
            .catch(() => setItems([]));
    }, [companyId, token]);

    useEffect(() => {
        if (companyId && branchId && token && fromDate && toDate) {
            if (new Date(fromDate) > new Date(toDate)) {
                setData([]);
                setFetched(false);
                setError('From Date cannot be greater than To Date');
                return;
            }
            fetchReport();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [companyId, branchId, token, itemId]);

    const fetchReport = async () => {
        if (!fromDate || !toDate) { setError('Please select date range'); return; }
        if (new Date(fromDate) > new Date(toDate)) { setError('From Date cannot be greater than To Date'); return; }
        setLoading(true);
        try {
            const res = await fetchData(
                'InventoryReportApi',
                `/StockLedgerDetails/${fromDate}/${toDate}/${companyId}/${branchId}/${itemId || 0}`,
                token
            );
            const rows = res?.response_data || res || [];
            setData(Array.isArray(rows) ? rows : []);
            setFetched(true);
        } catch (err) {
            console.error(err);
            setError('Failed to fetch stock ledger');
        } finally {
            setLoading(false);
        }
    };

    const filtered = useMemo(() => {
        if (!search.trim()) return data;
        const q = search.toLowerCase();
        return data.filter(r =>
            (r.ItemName || '').toLowerCase().includes(q) ||
            (r.Code || '').toLowerCase().includes(q) ||
            (r.TranType || '').toLowerCase().includes(q) ||
            (r.VoucherNo || '').toLowerCase().includes(q) ||
            (r.PartyName || '').toLowerCase().includes(q)
        );
    }, [data, search]);

    const totals = useMemo(() => filtered.reduce((acc, r) => ({
        InQty:  acc.InQty  + (parseFloat(r.InQty)  || 0),
        OutQty: acc.OutQty + (parseFloat(r.OutQty) || 0),
    }), { InQty: 0, OutQty: 0 }), [filtered]);

    const handleExport = async () => {
        if (!filtered.length) return;
        const headers = ['#', 'Date', 'Code', 'Item Name', 'Tran Type', 'Voucher No', 'Party Name', 'In Qty', 'Out Qty', 'Balance Qty'];
        const rows = filtered.map((r, i) => [
            i + 1, fmtDate(r.TranDate), r.Code, r.ItemName,
            r.TranType, r.VoucherNo, r.PartyName,
            r.InQty, r.OutQty, r.BalanceQty,
        ]);

        await exportStyledExcel({
            reportTitle: 'STOCK LEDGER',
            sheetName: 'Stock Ledger',
            fileName: `StockLedger_${fromDate}_${toDate}.xlsx`,
            companyName,
            username,
            fromDate,
            toDate,
            headers: headers,
            rows,
            totalsRow: ['', '', '', '', '', '', 'Totals', fmtQ(totals.InQty), fmtQ(totals.OutQty), ''],
            columnWidths: [4, 12, 10, 26, 16, 14, 22, 12, 12, 14],
        });
    };

    const thBase = { padding: '6px 8px', fontWeight: 700, fontSize: 11, whiteSpace: 'nowrap', border: '1px solid #4db6ac', color: '#fff', background: PRIMARY_MID };
    const tdBase = { padding: '5px 8px', fontSize: 11, whiteSpace: 'nowrap', border: '1px solid #e2e8f0' };

    return (
        <div style={{ minHeight: '100%', background: '#f0f2f5', padding: 12, fontFamily: "'Inter', sans-serif", boxSizing: 'border-box' }}>
            <LogoLoader open={loading} message="Loading stock ledger..." />
            <Snackbar open={!!error} autoHideDuration={5000} onClose={() => setError('')} anchorOrigin={{ vertical: 'top', horizontal: 'center' }}>
                <Alert severity="error" onClose={() => setError('')} sx={{ width: '100%' }}>{error}</Alert>
            </Snackbar>

            <div style={{ background: '#fff', borderRadius: 10, boxShadow: '0 4px 12px rgba(0,0,0,0.08)', border: '1px solid #e2e8f0', overflow: 'hidden' }}>

                {/* Toolbar */}
                <div className="report-toolbar" style={{ display: 'flex', alignItems: 'center', padding: '6px 12px', background: `linear-gradient(135deg, ${PRIMARY} 0%, ${PRIMARY_MID} 100%)`, gap: 6, flexWrap: 'nowrap', overflowX: 'auto', overflowY: 'hidden', scrollbarWidth: 'none' }}>
                    <Tooltip title="Back" arrow>
                        <IconButton onClick={() => history.push('/ReportsHub')} size="small" sx={iconBtn}>
                            <ArrowBack sx={{ fontSize: 16 }} />
                        </IconButton>
                    </Tooltip>
                    <span style={{ fontSize: 13, fontWeight: 700, color: '#fff', whiteSpace: 'nowrap', flexShrink: 0 }}>
                        Stock Ledger
                        <span style={{ fontSize: 11, fontWeight: 400, opacity: 0.8, display: 'block', lineHeight: 1.2 }}>Inventory Report</span>
                    </span>
                    <div style={{ flex: 1, minWidth: 4 }} />
                    <SearchableDropdown
                        value={itemId}
                        onChange={v => setItemId(v)}
                        options={[{ value: '0', label: 'All Items' }, ...items.map(it => ({ value: String(it.Id), label: `${it.Code} - ${it.Name}` }))]}
                        placeholder="All Items"
                        minWidth={200}
                        primaryColor={PRIMARY}
                    />
                    <input placeholder="Search item / party / voucher..."
                        value={search} onChange={e => setSearch(e.target.value)}
                        style={{ height: 29, border: '1px solid rgba(255,255,255,0.3)', borderRadius: 8, padding: '0 10px', fontSize: 12, backgroundColor: 'rgba(255,255,255,0.1)', color: '#fff', colorScheme: 'dark', flexShrink: 0, minWidth: 180, outline: 'none' }} />
                    <input type="date" value={fromDate} onChange={e => setFromDate(e.target.value)}
                        style={{ height: 29, border: '1px solid rgba(255,255,255,0.3)', borderRadius: 8, padding: '0 8px', fontSize: 12, backgroundColor: 'rgba(255,255,255,0.1)', color: '#fff', colorScheme: 'dark', flexShrink: 0 }} />
                    <input type="date" value={toDate} onChange={e => setToDate(e.target.value)}
                        style={{ height: 29, border: '1px solid rgba(255,255,255,0.3)', borderRadius: 8, padding: '0 8px', fontSize: 12, backgroundColor: 'rgba(255,255,255,0.1)', color: '#fff', colorScheme: 'dark', flexShrink: 0 }} />
                    <Tooltip title="Refresh" arrow>
                        <IconButton onClick={fetchReport} disabled={loading} size="small" sx={iconBtn}>
                            <Refresh sx={{ fontSize: 16 }} />
                        </IconButton>
                    </Tooltip>
                    <Tooltip title="Export Excel" arrow>
                        <IconButton onClick={handleExport} disabled={!filtered.length} size="small" sx={iconBtn}>
                            <GetApp sx={{ fontSize: 16 }} />
                        </IconButton>
                    </Tooltip>
                </div>

                {/* Summary bar */}
                {fetched && filtered.length > 0 && (
                    <div style={{ display: 'flex', gap: 8, padding: '6px 14px', borderBottom: '1px solid #f1f5f9', flexWrap: 'wrap' }}>
                        {[
                            { label: 'Transactions', value: filtered.length,       color: PRIMARY },
                            { label: 'Total In',     value: fmtQ(totals.InQty),    color: '#059669' },
                            { label: 'Total Out',    value: fmtQ(totals.OutQty),   color: '#dc2626' },
                        ].map(s => (
                            <div key={s.label} style={{ background: PRIMARY_LT, borderRadius: 8, padding: '4px 12px', display: 'flex', flexDirection: 'column', alignItems: 'center', minWidth: 110 }}>
                                <span style={{ fontSize: 10, color: '#64748b', fontWeight: 600 }}>{s.label}</span>
                                <span style={{ fontSize: 12, color: s.color, fontWeight: 700 }}>{s.value}</span>
                            </div>
                        ))}
                    </div>
                )}

                {/* Meta */}
                {fetched && (
                    <div style={{ padding: '4px 14px', fontSize: 11, color: '#64748b', borderBottom: '1px solid #f1f5f9' }}>
                        {fromDate} to {toDate} &nbsp;|&nbsp; {filtered.length} transactions
                        {search && ` (filtered from ${data.length})`}
                    </div>
                )}

                {/* Table */}
                <div className="report-table-scroll" style={{ overflow: 'auto', overflowY: 'scroll', maxHeight: 'calc(100vh - 190px)' }}>
                    <table style={{ borderCollapse: 'collapse', fontSize: 11, minWidth: 950, width: '100%' }}>
                        <thead style={{ position: 'sticky', top: 0, zIndex: 3 }}>
                            <tr>
                                <th style={{ ...thBase, textAlign: 'center', width: 36 }}>#</th>
                                <th style={{ ...thBase, textAlign: 'left' }}>Date</th>
                                <th style={{ ...thBase, textAlign: 'left', minWidth: 200 }}>Item</th>
                                <th style={{ ...thBase, textAlign: 'left' }}>Tran Type</th>
                                <th style={{ ...thBase, textAlign: 'left' }}>Voucher No</th>
                                <th style={{ ...thBase, textAlign: 'left', minWidth: 160 }}>Party Name</th>
                                <th style={{ ...thBase, textAlign: 'right' }}>In Qty</th>
                                <th style={{ ...thBase, textAlign: 'right' }}>Out Qty</th>
                                <th style={{ ...thBase, textAlign: 'right', background: '#004d40' }}>Balance Qty</th>
                            </tr>
                        </thead>
                        <tbody>
                            {filtered.length === 0 ? (
                                <tr>
                                    <td colSpan={9} style={{ textAlign: 'center', padding: 32, color: '#94a3b8', fontSize: 13 }}>
                                        {fetched ? 'No records found' : 'Select date range and click Refresh'}
                                    </td>
                                </tr>
                            ) : filtered.map((r, i) => {
                                const tranColor = TRAN_COLORS[(r.TranType || '').toUpperCase()] || '#374151';
                                return (
                                    <tr key={i} style={{ background: i % 2 === 0 ? '#fff' : PRIMARY_LT }}>
                                        <td style={{ ...tdBase, textAlign: 'center', color: '#94a3b8' }}>{i + 1}</td>
                                        <td style={{ ...tdBase, color: '#374151' }}>{fmtDate(r.TranDate)}</td>
                                        <td style={{ ...tdBase, fontWeight: 600, color: PRIMARY }}>
                                            <span style={{ color: '#64748b', fontSize: 10, marginRight: 4 }}>{r.Code}</span>{r.ItemName || '-'}
                                        </td>
                                        <td style={{ ...tdBase }}>
                                            <span style={{ background: `${tranColor}18`, color: tranColor, padding: '2px 8px', borderRadius: 10, fontSize: 10, fontWeight: 700 }}>
                                                {r.TranType || '-'}
                                            </span>
                                        </td>
                                        <td style={{ ...tdBase, color: '#374151', fontWeight: 500 }}>{r.VoucherNo || '-'}</td>
                                        <td style={{ ...tdBase, color: '#64748b' }}>{r.PartyName || '-'}</td>
                                        <td style={{ ...tdBase, textAlign: 'right', color: '#059669', fontWeight: 600 }}>{parseFloat(r.InQty) ? fmtQ(r.InQty) : '-'}</td>
                                        <td style={{ ...tdBase, textAlign: 'right', color: '#dc2626', fontWeight: 600 }}>{parseFloat(r.OutQty) ? fmtQ(r.OutQty) : '-'}</td>
                                        <td style={{ ...tdBase, textAlign: 'right', fontWeight: 700, color: PRIMARY }}>{fmtQ(r.BalanceQty)}</td>
                                    </tr>
                                );
                            })}
                        </tbody>
                        {filtered.length > 0 && (
                            <tfoot>
                                <tr style={{ background: PRIMARY_LT, fontWeight: 700 }}>
                                    <td colSpan={6} style={{ ...tdBase, fontWeight: 700, color: PRIMARY, textAlign: 'right' }}>Totals</td>
                                    <td style={{ ...tdBase, textAlign: 'right', color: '#059669', fontWeight: 700 }}>{fmtQ(totals.InQty)}</td>
                                    <td style={{ ...tdBase, textAlign: 'right', color: '#dc2626', fontWeight: 700 }}>{fmtQ(totals.OutQty)}</td>
                                    <td style={{ ...tdBase }} />
                                </tr>
                            </tfoot>
                        )}
                    </table>
                </div>
            </div>
        </div>
    );
};

export default StockLedger;
